import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ClerkProvider, AuthenticateWithRedirectCallback, useClerk } from '@clerk/clerk-react';
import { Toaster } from 'sonner';
import { HelmetProvider } from 'react-helmet-async';

import { useVersionCheck } from './hooks/useVersionCheck';
import { captureBookingSource } from './utils/bookingSource';

// Pages
import Landing from './pages/Landing';
import Login from './pages/Login';
import Register from './pages/Register';
import AuthCallback from './pages/AuthCallback';
import Discovery from './pages/Discovery';
import ServiceDetail from './pages/ServiceDetail';
import ProviderProfile from './pages/ProviderProfile';
import ProviderDashboard from './pages/ProviderDashboard';
import ProviderOnboarding from './pages/ProviderOnboarding';
import ClientDashboard from './pages/ClientDashboard';
import AdminDashboard from './pages/AdminDashboard';
import BookingSuccess from './pages/BookingSuccess';
import GuestBookingManage from './pages/GuestBookingManage';
import Messages from './pages/Messages';
import Settings from './pages/Settings';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import CookiePolicy from './pages/CookiePolicy';
import RefundPolicy from './pages/RefundPolicy';
import HelpCentre from './pages/HelpCentre';
import Contact from './pages/Contact';
import ForgotPassword from './pages/ForgotPassword';
import ClerkComplete from './pages/ClerkComplete';
import ReferralLanding from './pages/ReferralLanding';
import VideoFeed from './pages/VideoFeed';
import TwoFactorVerify from './pages/TwoFactorVerify';
import TwoFactorSetup from './pages/TwoFactorSetup';
import TrustCentre from './pages/TrustCentre';
import ForBarbers from './pages/ForBarbers';
import CityServicePage from './pages/CityServicePage';
import SafetyTracker from './pages/SafetyTracker';
import Roadmap from './pages/Roadmap';
import CoOpVerify from './pages/CoOpVerify';
import CoOpCheckout from './pages/CoOpCheckout';
import InstallPrompt from './components/pwa/InstallPrompt';
import { BackToTop } from './components/BackToTop';
import CookieConsentBanner from './components/compliance/CookieConsentBanner';

// Wrapper to handle @username routes. Session-level booking-source
// attribution lives in utils/bookingSource, it's captured once per
// browser session and locked for the whole visit.
function CatchAllRoute() {
  const location = useLocation();
  const path = location.pathname;

  // Handle /@username paths, provider's personal booking link.
  if (path.match(/^\/@[\w.]+$/)) {
    captureBookingSource({ pathname: path, search: location.search });
    return <ProviderProfile />;
  }

  return <Navigate to="/" replace />;
}

import './App.css';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};

// API instance with credentials, uses relative path so cookies work on any domain
export const api = axios.create({
  baseURL: '/api',
  withCredentials: true
});

// Cookie-based auth: withCredentials sends httpOnly cookies automatically.
// No localStorage interceptor needed.

// Auth Provider
function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  // Beta-mode flag, fetched once at mount from /api/beta/status.
  // Used by dashboards to hide Pro upgrade UI, treat all approved
  // providers as Pro-equivalent, and swap pricing copy.
  const [betaConfig, setBetaConfig] = useState({ beta_mode: false, name: '', features: [] });

  const checkAuth = useCallback(async (retries = 5) => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    
    try {
      const response = await api.get('/auth/me');
      setUser(response.data);
    } catch (error) {
      // Retry on transient failures (503, 502, network error), backend restarting during deploy
      const status = error.response?.status;
      const isTransient = status === 503 || status === 502 || !error.response;
      if (retries > 0 && isTransient) {
        setTimeout(() => checkAuth(retries - 1), 3000);
        return;
      }
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
    // Fire-and-forget beta status probe. Safe to call unauth.
    api.get('/beta/status')
      .then(res => setBetaConfig(res.data || { beta_mode: false }))
      .catch(() => setBetaConfig({ beta_mode: false }));
  }, [checkAuth]);

  const login = useCallback(async (email, password) => {
    const response = await api.post('/auth/login', { email, password });
    // If 2FA is required the backend returns a challenge object instead of a
    // logged-in user. Don't set the user, the Login page will redirect to
    // /auth/2fa with the challenge token.
    if (response.data?.requires_2fa) {
      return { ...response.data, requires_2fa: true };
    }
    setUser(response.data);
    return response.data;
  }, []);

  const register = useCallback(async (email, password, name, role) => {
    const response = await api.post('/auth/register', { email, password, name, role });
    setUser(response.data);
    return response.data;
  }, []);

  const guestLogin = useCallback(async (name = 'Guest') => {
    const response = await api.post('/auth/guest', { name });
    setUser(response.data);
    return response.data;
  }, []);

  const clerk = useClerk();
  const logout = useCallback(async () => {
    try {
      await api.post('/auth/logout');
    } catch (error) {
      console.error('Logout error:', error);
    }
    try {
      await clerk.signOut();
    } catch (error) {
      console.error('Clerk sign-out error:', error);
    }
    setUser(null);
  }, [clerk]);

  const updateUser = useCallback((userData) => {
    setUser(userData);
  }, []);

  const value = useMemo(() => ({
    user, loading, betaConfig, login, register, guestLogin, logout, updateUser, checkAuth
  }), [user, loading, betaConfig, login, register, guestLogin, logout, updateUser, checkAuth]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

// Protected Route
function ProtectedRoute({ children, allowedRoles }) {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAFAFA]">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#D4AF37]"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user.role)) {
    return <Navigate to="/" replace />;
  }

  return children;
}

// App Router
function AppRouter() {
  const location = useLocation();
  const { user } = useAuth();

  // Version check, shows toast when new build deployed
  useVersionCheck();

  // Capture the booking-source on the first render of the session.
  // captureBookingSource() is idempotent, no-op after the first call,
  // and inspects window.location so /@username paths still resolve to
  // "direct" while discover/search/category land on "marketplace".
  useEffect(() => {
    captureBookingSource({ pathname: location.pathname, search: location.search });
    // We intentionally only run this once, the helper itself is
    // session-locked so additional calls within the same session
    // won't overwrite anything.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Check URL fragment for session_id (OAuth callback)
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/sso-callback" element={
        <AuthenticateWithRedirectCallback
          signInFallbackRedirectUrl="/auth/complete" signUpFallbackRedirectUrl="/auth/complete" />
      } />
      <Route path="/auth/complete" element={<ClerkComplete />} />
      <Route path="/auth/2fa" element={<TwoFactorVerify />} />
      <Route path="/auth/2fa/setup" element={
        <ProtectedRoute>
          <TwoFactorSetup />
        </ProtectedRoute>
      } />
      <Route path="/discover" element={<Discovery />} />
      <Route path="/feed" element={<VideoFeed />} />
      <Route path="/service/:serviceId" element={<ServiceDetail />} />
      <Route path="/s/:serviceSlug" element={<ServiceDetail />} />
      <Route path="/provider/:providerId" element={<ProviderProfile />} />
      <Route path="/booking/success" element={<BookingSuccess />} />
      <Route path="/booking/manage" element={<GuestBookingManage />} />
      <Route path="/r/:code" element={<ReferralLanding />} />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/terms" element={<TermsOfService />} />
      <Route path="/cookies" element={<CookiePolicy />} />
      <Route path="/refunds" element={<RefundPolicy />} />
      <Route path="/help" element={<HelpCentre />} />
      <Route path="/contact" element={<Contact />} />
      <Route path="/trust" element={<TrustCentre />} />
      <Route path="/for-barbers" element={<ForBarbers />} />
      <Route path="/safety/track/:token" element={<SafetyTracker />} />
      <Route path="/roadmap" element={<Roadmap />} />
      <Route path="/co-op/verify" element={<CoOpVerify />} />
      <Route path="/co-op/book/:serviceId" element={<CoOpCheckout />} />
      
      <Route path="/provider/dashboard" element={
        <ProtectedRoute allowedRoles={['provider', 'admin']}>
          <ProviderDashboard />
        </ProtectedRoute>
      } />
      
      <Route path="/provider/onboarding" element={
        <ProtectedRoute allowedRoles={['provider', 'admin']}>
          <ProviderOnboarding />
        </ProtectedRoute>
      } />
      
      <Route path="/dashboard" element={
        <ProtectedRoute allowedRoles={['client']}>
          <ClientDashboard />
        </ProtectedRoute>
      } />
      
      <Route path="/messages" element={
        <ProtectedRoute>
          <Messages />
        </ProtectedRoute>
      } />

      <Route path="/settings" element={
        <ProtectedRoute>
          <Settings />
        </ProtectedRoute>
      } />
      
      <Route path="/admin/z-control" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <AdminDashboard />
        </ProtectedRoute>
      } />
      <Route path="/admin" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <Navigate to="/admin/z-control" replace />
        </ProtectedRoute>
      } />

      {/* Programmatic-SEO city x service landing pages.
          Registered AFTER every specific route so it can't shadow
          /admin, /provider, /booking, etc. The page itself validates
          the slug pair against the seo_cities + seo_services allow-list
          and redirects to /, on any unknown city or service. */}
      <Route path="/:citySlug/:serviceSlug" element={<CityServicePage />} />

      <Route path="*" element={<CatchAllRoute />} />
    </Routes>
  );
}

function ClerkWithRouter({ children }) {
  const navigate = useNavigate();
  return (
    <ClerkProvider
      publishableKey={process.env.REACT_APP_CLERK_PUBLISHABLE_KEY}
      routerPush={(to) => navigate(to)}
      routerReplace={(to) => navigate(to, { replace: true })}
      afterSignOutUrl="/"
    >
      {children}
    </ClerkProvider>
  );
}

function App() {
  return (
    <HelmetProvider>
      <BrowserRouter>
        <ClerkWithRouter>
        <AuthProvider>
          <Toaster position="top-right" richColors />
          <AppRouter />
          <InstallPrompt />
          <BackToTop />
          <CookieConsentBanner />
        </AuthProvider>
        </ClerkWithRouter>
      </BrowserRouter>
    </HelmetProvider>
  );
}

export default App;
